<script setup lang="ts">
interface SizeOption {
  label: string
  value: string
  /** Whether this size can be bought online. Left unset where stock is per product, not per size. */
  available?: boolean
}

const emit = defineEmits(['close'])

const props = defineProps<{
  sizes: SizeOption[]
  productStoreLocation: ProductStoreLocation
  sizeGuide?: any[]
  columnsPerRow?: number
}>()

const selectedSize = defineModel<string>()
const openAccordion = ref(false)
const sizeGuideAccordion = ref<{ $el: HTMLElement } | null>(null)

const handleSizeSelection = (value: string) => {
  selectedSize.value = value
  emit('close')
}

const handleSizeGuideClick = async () => {
  openAccordion.value = !openAccordion.value

  if (!openAccordion.value) {
    await nextTick()
    openAccordion.value = true
  }

  requestAnimationFrame(() => {
    sizeGuideAccordion.value?.$el.scrollIntoView({ behavior: 'smooth' })
  })
}
</script>

<template>
  <div v-if="sizes.length > 1" class="product-size-selector">
    <h1 class="product-size-selector__heading">{{ $t('product.selector.select_size_text') }}</h1>

    <p class="product-size-selector__help">
      {{ $t('product.size_guide.need_help') }}
      <Button type="button" @click="handleSizeGuideClick">
        {{ $t('product.size_guide.heading') }}
      </Button>
    </p>

    <div class="product-size-selector__container" :style="{ '--grid-columns': columnsPerRow || 5 }">
      <Button
        v-for="size in sizes"
        variant="none"
        :key="size.label"
        class="product-size-selector__button"
        data-testid="pdp-size-option"
        :data-test-available="size.available"
        @click="handleSizeSelection(size.value)"
      >
        {{ size.label }}
      </Button>
    </div>
  </div>
  <Accordion v-if="sizeGuide?.length" :open="openAccordion" :ga-options="{ group: 'product' }" ref="sizeGuideAccordion">
    <template #title>
      <span class="product-size-guide">{{ $t('product.size_guide.heading') }}</span>
    </template>
    <ProductSizeGuide :size-guide="sizeGuide" :location="productStoreLocation" />
  </Accordion>
</template>

<style lang="postcss">
.product-size-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  .product-size-selector__heading {
    font-size: 1.75rem;
  }

  .product-size-selector__help {
    margin-bottom: var(--space-60);

    button {
      text-transform: lowercase;
    }
  }

  .product-size-selector__container {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(var(--grid-columns, 5), 1fr);
    padding-bottom: var(--space-60);
    border-bottom: 1px solid var(--ui-neutral-20);
  }

  .product-size-selector__button {
    border: 1px solid var(--ui-neutral-20);
    cursor: pointer;
    font-weight: bold;
    text-transform: uppercase;
    transition: all 0.3s;
    padding: var(--space-50) 0 1.05rem 0;
    width: 100%;

    &:hover {
      color: var(--text-link-hover);
      border: 1px solid var(--border-dark-30);
    }
  }
}

.product-size-guide {
  font-size: var(--text-base);
}
</style>
